import React, { useEffect, useMemo, useState } from "react"

import { FEATURE_FLAGS, useFeatureFlagValue } from "@/feature-flags"
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CompletionIndicator,
} from "@coval/ui"
import { AudioLines } from "lucide-react"

import type { PersonaMetadata } from "@/types/api/Persona"
import {
  COVAL_VOICES,
  getVoicePreviewUrl,
} from "@/types/ModelConfigs/CovalVoiceConstants"

import type { PersonaFormController } from "../hooks/usePersonaFormController"
import { usePersonaVoiceDesign } from "../hooks/usePersonaVoiceDesign"
import type { PersonaCustomVoiceOption } from "../types"
import { CustomVoiceOption } from "./PersonaCustomVoiceLibraryOptions"
import { getPreferredLanguageCodeForVoice } from "./personaVoiceLibraryUtils"
import { VoiceOption } from "./PersonaVoiceOptionCard"
import { PersonaVoicePickerDialog } from "./PersonaVoicePickerDialog"

/**
 * The persona's voice as a selection card, laid out like the launcher's
 * agent and test-set cards: empty until chosen, then the chosen voice shown
 * on the card's own surface rather than in a container of its own. Library
 * voices and custom designed voices both land here; browsing, previewing,
 * designing, and tuning volume and speed all happen in the dialog.
 */
export function PersonaVoiceCard({
  controller,
}: {
  controller: PersonaFormController
}) {
  const {
    selectedPersona,
    portableAssetsOnly,
    formData,
    isBuiltIn,
    isSpeechDisabled,
    metadata,
    updateMetadata,
    currentlyPlayingAudioId,
    setCurrentlyPlayingAudioId,
  } = controller
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const customVoiceFlag = useFeatureFlagValue(
    FEATURE_FLAGS.PERSONA_CUSTOM_VOICE_DESIGN,
  )
  const voiceDesign = usePersonaVoiceDesign()
  const canGenerateCustomVoice =
    customVoiceFlag === true && !isBuiltIn && !portableAssetsOnly

  const selectedVoice = useMemo(
    () => COVAL_VOICES.find((voice) => voice.name === metadata?.voice_name),
    [metadata?.voice_name],
  )

  // The persona record only stores the custom voice's identity, so rebuild the
  // option from metadata and keep it at the top of the list the picker shows.
  const personaMetadata = formData.metadata as PersonaMetadata
  const customVoiceId = portableAssetsOnly
    ? undefined
    : personaMetadata.custom_voice_id
  const customVoiceName = personaMetadata.custom_voice_name
  const customVoiceDescription = personaMetadata.custom_voice_description
  const customVoiceLanguage = personaMetadata.custom_voice_language
  const savedCustomVoice = useMemo(
    (): PersonaCustomVoiceOption | undefined =>
      customVoiceId
        ? {
            id: customVoiceId,
            name:
              customVoiceName || `${formData.display_name || "Persona"} voice`,
            description: customVoiceDescription || "Custom voice",
            language: customVoiceLanguage || "en",
          }
        : undefined,
    [
      customVoiceDescription,
      customVoiceId,
      customVoiceLanguage,
      customVoiceName,
      formData.display_name,
    ],
  )
  const [customVoices, setCustomVoices] = useState<PersonaCustomVoiceOption[]>(
    () => (savedCustomVoice ? [savedCustomVoice] : []),
  )

  useEffect(() => {
    if (!savedCustomVoice) {
      return
    }
    setCustomVoices((current) => [
      savedCustomVoice,
      ...current.filter((voice) => voice.id !== savedCustomVoice.id),
    ])
  }, [savedCustomVoice])

  const rememberCustomVoice = (voice: PersonaCustomVoiceOption) =>
    setCustomVoices((current) => [
      voice,
      ...current.filter((candidate) => candidate.id !== voice.id),
    ])

  const hasVoice = Boolean(selectedVoice || savedCustomVoice)

  const openPicker = () => setIsPickerOpen(true)
  const closePicker = () => {
    setCurrentlyPlayingAudioId(null)
    setIsPickerOpen(false)
  }
  const clearVoice = () => {
    setCurrentlyPlayingAudioId(null)
    updateMetadata({
      voice_name: undefined,
      custom_voice_id: undefined,
      custom_voice_name: undefined,
      custom_voice_description: undefined,
      custom_voice_language: undefined,
    })
  }

  return (
    <>
      <Card className="w-full">
        <CardHeader className="p-6">
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <AudioLines className="size-4 shrink-0" />
              Voice
            </CardTitle>
            <div className="flex shrink-0 items-center gap-3">
              {hasVoice && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={clearVoice}
                    disabled={isSpeechDisabled}
                    aria-label="Clear voice selection"
                  >
                    Clear
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={openPicker}
                    disabled={isSpeechDisabled}
                    data-testid="persona-voice-browse"
                  >
                    Change Voice
                  </Button>
                </>
              )}
              <CompletionIndicator complete={hasVoice} label="Voice" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-6 pb-6 pt-0">
          {savedCustomVoice ? (
            <CustomVoiceOption
              voice={savedCustomVoice}
              previewUrl={voiceDesign.previewUrls.get(savedCustomVoice.id)}
              previewFailed={voiceDesign.previewErrorIds.has(
                savedCustomVoice.id,
              )}
              getPreviewUrl={voiceDesign.getPreviewUrl}
              isSelected={false}
              isDisabled={isSpeechDisabled}
              currentlyPlayingAudioId={currentlyPlayingAudioId}
              onPlayingChange={setCurrentlyPlayingAudioId}
              onSelect={openPicker}
            />
          ) : selectedVoice ? (
            <VoiceOption
              voice={selectedVoice}
              previewUrl={
                selectedVoice.hasPreview === false
                  ? undefined
                  : getVoicePreviewUrl(
                      selectedVoice.name,
                      getPreferredLanguageCodeForVoice(
                        selectedVoice,
                        metadata?.language_code,
                      ),
                    )
              }
              voiceVolume={metadata?.voice_volume ?? undefined}
              voiceSpeed={metadata?.voice_speed ?? undefined}
              isSelected={false}
              isDisabled={isSpeechDisabled}
              currentlyPlayingAudioId={currentlyPlayingAudioId}
              onPlayingChange={setCurrentlyPlayingAudioId}
              onSelect={openPicker}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-2">
              <Button
                type="button"
                variant="secondary"
                onClick={openPicker}
                disabled={isSpeechDisabled}
                data-testid="persona-voice-browse"
              >
                Choose Voice
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <PersonaVoicePickerDialog
        isOpen={isPickerOpen}
        metadata={metadata}
        isNewPersona={!selectedPersona}
        currentlyPlayingAudioId={currentlyPlayingAudioId}
        onPlayingChange={setCurrentlyPlayingAudioId}
        onCancel={closePicker}
        onApply={(selection) => {
          setCurrentlyPlayingAudioId(null)
          updateMetadata(selection)
          setIsPickerOpen(false)
        }}
        customVoices={portableAssetsOnly ? [] : customVoices}
        canGenerateCustomVoice={canGenerateCustomVoice}
        voiceDesign={voiceDesign}
        onCustomVoiceCreated={rememberCustomVoice}
      />
    </>
  )
}
